/** Marsh catalog enum keys sent in quote requests; values are Marsh numeric IDs on server */

import { GeneralUtilities } from "../utilities/general";

export const MARSH_STATES: Record<string, number> = {
  AGUASCALIENTES: 1,
  BAJA_CALIFORNIA_NORTE: 2,
  BAJA_CALIFORNIA_SUR: 3,
  CAMPECHE: 4,
  COAHUILA: 5,
  COLIMA: 6,
  CHIAPAS: 7,
  CHIHUAHUA: 8,
  CDMX: 9,
  DISTRITO_FEDERAL: 9,
  DURANGO: 10,
  ESTADO_DE_MEXICO: 11,
  GUANAJUATO: 12,
  GUERRERO: 13,
  HIDALGO: 14,
  JALISCO: 15,
  MICHOACAN: 16,
  MORELOS: 17,
  NAYARIT: 18,
  NUEVO_LEON: 19,
  OAXACA: 20,
  PUEBLA: 21,
  QUERETARO: 22,
  QUINTANA_ROO: 23,
  SAN_LUIS_POTOSI: 24,
  SINALOA: 25,
  SONORA: 26,
  TABASCO: 27,
  TAMAULIPAS: 28,
  TLAXCALA: 29,
  VERACRUZ: 30,
  YUCATAN: 31,
  ZACATECAS: 32,
};

export const MARSH_PACKAGES = { ALL: 0, SPECIAL: 1 } as const;

export const MARSH_PAYMENT_FORMS = {
  MULTIANUAL_FINANCIADO: 6,
  MULTIANUAL_FRACCIONADO: 7,
  MULTIANUAL_CONTADO: 10,
  MULTIANUAL_FRACCIONADO_CON_POLIZA_DE_CONTADO: 11,
  MULTIANUAL_FRACCIONADO_CON_POLIZA_EXTERNA: 12,
  MULTIANUAL_FRACCIONADO_CON_POLIZA_GRATIS_BNP: 13,
} as const;

export const MARSH_USES = {
  PARTICULAR: 1,
  PUBLICO: 2,
  CARGA_A: 3,
  CARGA_B: 4,
  CARGA_C: 5,
  TURISMO: 6,
  COMERCIAL: 7,
  ESCOLAR: 10,
  PERSONAL: 18,
} as const;

export const MARSH_UNIT_TYPES = {
  AUTO: 1,
  CAMION: 2,
  MOTOCICLETA: 3,
  AUTOBUS: 4,
  RABON: 5,
  TANDEM: 6,
  THORTON: 7,
  TRACTOCAMION: 8,
  REMOLQUE: 9,
  VAN: 10,
  SEMIREMOLQUE: 11,
  TANQUES: 15,
  PLATAFORMAS: 16,
  TRACTOCAMION_2ND_REMOLQUE: 17,
  MIDIBUS: 18,
} as const;

export const MARSH_OPERATIONS = { CASH: 1, FINANCING: 2, RENEWAL: 3, LEASE: 4 } as const;

export const MARSH_POLICY_TYPES = { NEW: 1, RENEWAL: 2 } as const;

export const MARSH_EMPLOYEES = { RETAIL: 1, EMPLOYEES: 2 } as const;

export const MARSH_TAX_TYPES = { FRONTERIZA: 1, NORMAL: 2, CODIGO_POSTAL: 3 } as const;

export const MARSH_PERSON_TYPES = { PHYSICAL: 1, MORAL: 2 } as const;

export const MARSH_GENDERS = { MALE: 1, FEMALE: 2, NOT_APPLICABLE: 0 } as const;

export const MARSH_MARITAL_STATUSES = {
  SINGLE: 1,
  MARRIED: 2,
  WIDOWED: 3,
  DIVORCED: 4,
  NOT_APPLICABLE: 0,
} as const;

export const MARSH_BIRTH_COUNTRIES = { MEXICO: 1 } as const;

/** Default CFDI fiscal values for Marsh issue_policy billing */
export const MARSH_FISCAL_DEFAULTS = {
  PF: { REGIMEN_FISCAL: 1, REGIMEN_SOCIETARIO: 1, USO_CFDI_ID: 24 },
  PM: { REGIMEN_FISCAL: 23, REGIMEN_SOCIETARIO: "SA DE CV", USO_CFDI_ID: 24, IDCIF: 99999999 },
} as const;

/** Default Art. 140 answers for Marsh issue_policy */
export const MARSH_ART140_DEFAULTS = {
  ART140_PREGUNTA1: 1,
  ART140_PREGUNTA2: 1,
  ART140_PREGUNTA3: 1,
} as const;

/** Maps invoice_information.tax_regime keys to Marsh REGIMEN_FISCAL IDs */
export const MARSH_TAX_REGIME_MAP: Record<string, number> = {
  salaries_and_salaries_and_income_assimilated_to_wages: 1,
  moral_people_general: 23,
  moral_people_for_non_profit_purposes: 23,
  natural_persons_with_business_and_professional_activities: 7,
  lease: 2,
  no_tax_obligations: 10,
};

/**
 * state ID from a Mexican postal code for marsh API requests.
 * Falls back to CDMX (9) when the state cannot be determined.
 */
export function getMarshStateIdFromPostalCode(postalCode: string | number | null | undefined): number {
  const cp = String(postalCode ?? "")
    .replace(/\D/g, "")
    .slice(0, 5);
  if (!cp) {
    return MARSH_STATES.CDMX;
  }
  const stateKey = GeneralUtilities.stateFromPostalCode(cp);
  return getMarshStateIdFromStateKey(stateKey);
}

/** Resolves Marsh state ID from a GeneralUtilities state key (e.g. "CDMX", "JALISCO"). */
export function getMarshStateIdFromStateKey(stateKey: string | null | undefined): number {
  if (!stateKey) {
    return MARSH_STATES.CDMX;
  }
  const normalized = stateKey.toUpperCase().replace(/\s+/g, "_");
  return MARSH_STATES[normalized] ?? MARSH_STATES.CDMX;
}

/** Default quote values for Kia Trust alliance */
export const MARSH_KIA_TRUST_DEFAULTS = {
  package_id: "ALL",
  payment_form_id: "MULTIANUAL_FRACCIONADO",
  use_id: "PARTICULAR",
  unit_type_id: "AUTO",
  operation_id: "FINANCING",
  new_id: "NEW",
  employee_id: "RETAIL",
  tax_type_id: "NORMAL",
  state_id: "CDMX",
} as const;
